import { supabase, HOUSEHOLD_COLUMNS, type Household } from '../lib/supabase'
import { buildExport, exportFileName, type ExportRows } from '../lib/dataExport'

// Settings › Your data: reads everything the household has, once, under RLS
// (so only its own rows), and hands the member a JSON file (lib/dataExport.ts).
// Never plan-gated: a household's data is its own on any plan, like forget_items.

const TRIP_COLUMNS =
  'id, household_id, store_id, started_by, started_at, finished_at, finished_on, finished_by, items, cost, currency, updated_at, picked_order, deleted_at'
/** PostgREST answers at most this many rows a request (config.toml max_rows), so tables are read a page at a time. */
const PAGE = 1000
/** A household with more than this in one table is no household; stop rather than loop. */
const MAX_PAGES = 200

export const EXPORT_FAILED = 'Couldn’t gather your data. Check your connection and try again.'

/** download(): the household's file, saved by the browser; an error to show, or null. */
export function useDataExport(householdId: string) {
  async function read<T>(query: PromiseLike<{ data: unknown; error: unknown }>): Promise<T> {
    const { data, error } = await query
    if (error) throw error
    return data as T
  }

  // Every row of a household's table, page by page in a stable order, so a
  // long item history isn't cut off at the server's row cap.
  async function all<T>(table: string, columns = '*'): Promise<T[]> {
    const rows: T[] = []
    for (let page = 0; page < MAX_PAGES; page++) {
      const from = page * PAGE
      const batch = await read<T[]>(
        supabase.from(table).select(columns).eq('household_id', householdId).order('id').range(from, from + PAGE - 1),
      )
      rows.push(...batch)
      if (batch.length < PAGE) break
    }
    return rows
  }

  async function download(): Promise<string | null> {
    try {
      const [household, members, categories, items, recipes, plannedMeals, mealTemplates, stores, trips] = await Promise.all([
        read<Household>(supabase.from('households').select(HOUSEHOLD_COLUMNS).eq('id', householdId).single()),
        all<ExportRows['members'][number]>('members'),
        all<ExportRows['categories'][number]>('categories'),
        all<ExportRows['items'][number]>('items'),
        all<ExportRows['recipes'][number]>('recipes'),
        all<ExportRows['plannedMeals'][number]>('planned_meals'),
        all<ExportRows['mealTemplates'][number]>('meal_templates'),
        all<ExportRows['stores'][number]>('stores'),
        all<ExportRows['trips'][number]>('trips', TRIP_COLUMNS),
      ])
      const now = new Date()
      const file = buildExport({ household, members, categories, items, recipes, plannedMeals, mealTemplates, stores, trips }, now)
      const url = URL.createObjectURL(new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' }))
      const a = document.createElement('a')
      a.href = url
      a.download = exportFileName(household.name, now)
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
      return null
    } catch {
      return EXPORT_FAILED
    }
  }

  return { download }
}
